/**
 * cognito.js — Authentication API
 *
 * OAuth 2.0 + PKCE shim (Phase 2):
 *   When window.OAuthConfig?.enabled is true, all login/logout/session methods
 *   delegate to oauthClient + oauthTokenStorage instead of the Cognito SDK.
 *
 *   Authenticated user operations (changePassword, changeEmail, updateUserDetails,
 *   retrieveUserAttributes, verifyChangedEmail) always use the SDK — they require
 *   a live CognitoUserSession which is constructed by createUserSession().
 *
 * Feature flag:
 *   ALL OAuth code paths are guarded by `window.OAuthConfig?.enabled`.
 *   Setting OAUTH_ENABLED=false in .env instantly reverts to legacy SDK auth.
 */

import {
  CognitoUser,
  CognitoUserPool,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

import renderError from '../utils/renderErrorMessage';
import formatCognitoUserData from '../utils/formatCognitoUserData';
import formatCognitoAttributesData from '../utils/formatCognitoAttributesData';
import isDuplicateEmailError from '../helpers/isDuplicateEmailError';
import {
  buildAuthorizeUrl,
  refreshTokens,
  refreshTokensViaCognitoApi,
  buildLogoutUrl,
  globalSignOut,
} from './oauthClient';
import {
  saveTokens,
  getTokens,
  getRefreshToken,
  clearTokens,
  getTokenSource,
  hasValidTokens,
} from '../utils/oauthTokenStorage';
import formatOAuthTokenData from '../utils/formatOAuthTokenData';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Return a Cognito user pool initialised for the current context.
 * Selects the eTrain client when the stored redirect URI references training.
 *
 * @returns {CognitoUserPool}
 */
const getUserPool = () => {
  const data =
    localStorage.ffaRedirectUri &&
    localStorage.ffaRedirectUri.includes('learning', 'learn', 'training')
      ? { UserPoolId: CognitoUserPoolId, ClientId: CognitoClientIdEtrain }
      : { UserPoolId: CognitoUserPoolId, ClientId: CognitoClientIdPlayfootball };

  return new CognitoUserPool(data);
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export default {
  /**
   * Initiate login.
   *
   * OAuth path: redirects to Cognito Hosted UI with PKCE.
   * SDK path: authenticates directly via amazon-cognito-identity-js.
   *
   * @param {object} credentials
   * @param {string} credentials.email
   * @param {string} credentials.password
   * @returns {Promise<void|object>}
   */
  login: async ({ email, password }) => {
    console.info('COGNITO_login');
    console.time('COGNITO_login');

    if (window.OAuthConfig?.enabled) {
      const url = await buildAuthorizeUrl({
        prompt: 'login',
        state: window.location.pathname,
      });
      window.location.href = url;
      return;
    }

    // SDK path — unchanged
    const username = email;
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: getUserPool(),
    });

    const authenticationDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: function (result) {
          console.log('login.result', result);
          resolve(formatCognitoUserData(result));
        },
        onFailure: function (err) {
          console.log('login.err', err);
          reject({ global: renderError(err) });
        },
      });
    });
  },

  /**
   * Initiate registration.
   *
   * OAuth path: redirects to Cognito Hosted UI (handles sign-up natively).
   * SDK path: calls userPool.signUp directly.
   *
   * @param {object} userData
   * @returns {Promise<void|object>}
   */
  register: async (userData) => {
    console.info('COGNITO_register');
    console.time('COGNITO_register');

    // Registration always uses the Cognito SDK directly — it is not part of the
    // OAuth login flow. The signUp API call creates the Cognito account; the user
    // is then directed to the verification page to confirm their email code.
    userData.email = userData.email.toLowerCase();

    const attributeList = [
      new CognitoUserAttribute({ Name: 'given_name', Value: userData.firstName }),
      new CognitoUserAttribute({ Name: 'family_name', Value: userData.lastName }),
      new CognitoUserAttribute({ Name: 'email', Value: userData.email.toLowerCase() }),
      new CognitoUserAttribute({ Name: 'birthdate', Value: userData.dateOfBirth }),
      new CognitoUserAttribute({ Name: 'custom:postcode', Value: userData.postcode }),
      new CognitoUserAttribute({
        Name: 'custom:marketing_optin',
        Value: userData.emailOpt ? 'true' : 'false',
      }),
      new CognitoUserAttribute({
        Name: 'custom:is_first_login',
        Value: userData.isFirstLogin ? 'true' : 'false',
      }),
    ];

    console.time('TIME_calling_userPool.signUp');
    return new Promise((resolve, reject) => {
      getUserPool().signUp(
        userData.email.toLowerCase(),
        userData.password,
        attributeList,
        null,
        function (err, result) {
          console.timeEnd('TIME_calling_userPool.signUp');
          console.log('Error!', err);
          if (err) {
            if (isDuplicateEmailError(err.message)) {
              reject({ login: renderError(err) });
            } else {
              reject({ global: renderError(err) });
            }
            return;
          }
          resolve(userData);
        }
      );
    });
  },

  /**
   * Sign the user out.
   *
   * OAuth path: clears app tokens then redirects to Cognito global logout
   *   (clears the Cognito session cookie preventing silent re-auth).
   * SDK path: calls cognitoUser.signOut() (local only).
   *
   * @returns {Promise<void>}
   */
  logout: () => {
    console.info('COGNITO_logout');
    console.time('COGNITO_logout');

    if (window.OAuthConfig?.enabled) {
      // Revoke all sessions for this user server-side so other apps (e.g. testapp)
      // detect the sign-out on their next visibilitychange refresh attempt.
      // Fire-and-forget — local clear and redirect always proceed regardless.
      // Only call GlobalSignOut when we have a valid (non-expired) access token.
      // Cognito rejects GlobalSignOut with 400 NotAuthorizedException for expired tokens.
      const tokens = getTokens();
      if (tokens && hasValidTokens()) {
        globalSignOut(tokens.accessToken).catch(() => {});
      }
      clearTokens();
      window.location.href = buildLogoutUrl();
      return Promise.resolve();
    }

    // SDK path — unchanged
    const cognitoUser = getUserPool().getCurrentUser();

    return new Promise((resolve) => {
      if (cognitoUser !== null) {
        cognitoUser.signOut();
      }
      resolve();
    });
  },

  /**
   * Refresh the current session tokens.
   *
   * OAuth path: calls oauthClient.refreshTokens, persists new tokens,
   *   returns { accessToken, idToken, refreshToken } (shape the REFRESH_SESSION
   *   Vuex action expects).
   * SDK path: uses CognitoRefreshToken + cognitoUser.refreshSession.
   *
   * @param {object} params
   * @param {string} params.refreshToken
   * @returns {Promise<{accessToken: string, idToken: string, refreshToken: string}>}
   */
  refreshSession: async ({ refreshToken }) => {
    console.info('COGNITO_refreshSession');
    console.time('COGNITO_refreshSession');

    if (window.OAuthConfig?.enabled) {
      // Skip the Cognito refresh call when the stored access token has not
      // expired yet — this avoids unnecessary API calls and prevents a failed
      // refresh from blocking updates immediately after login.
      if (hasValidTokens()) {
        const current = getTokens();
        return {
          accessToken: current.accessToken,
          idToken: current.idToken,
          refreshToken: current.refreshToken,
        };
      }

      // Route to the correct Cognito refresh endpoint based on how the tokens
      // were originally obtained:
      //   'pkce' — Hosted UI authorization_code flow → use oauth2/token endpoint
      //   'sdk'  — auth-ui SDK auth (SRP / CUSTOM_AUTH) → use InitiateAuth API
      // The oauth2/token hosted-UI endpoint rejects SDK-issued refresh tokens.
      const tokenSource = getTokenSource();
      const response = tokenSource === 'pkce'
        ? await refreshTokens(refreshToken)
        : await refreshTokensViaCognitoApi(refreshToken);

      const newTokens = {
        accessToken: response.access_token,
        idToken: response.id_token,
        refreshToken: response.refresh_token,
      };
      saveTokens(newTokens);
      return newTokens;
    }

    // SDK path — unchanged
    const cognitoUser = getUserPool().getCurrentUser();
    const RefreshToken = new CognitoRefreshToken({ RefreshToken: refreshToken });

    return new Promise((resolve, reject) => {
      if (cognitoUser === null) {
        reject();
        return;
      }

      cognitoUser.refreshSession(RefreshToken, function (err, session) {
        if (err) {
          reject({ global: 'Sorry, Something Went Wrong: Please try again later.' });
          return;
        }

        resolve({
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.idToken.jwtToken,
          refreshToken: session.getRefreshToken().token,
        });
      });
    });
  },

  /**
   * Retrieve the currently authenticated user's data.
   *
   * OAuth path: reads tokens from oauthTokenStorage, formats via
   *   formatOAuthTokenData, returns the canonical user shape.
   * SDK path: calls cognitoUser.getSession then formatCognitoUserData.
   *
   * @returns {Promise<object>} Formatted user data
   */
  retrieveCurrentUser: () => {
    console.info('COGNITO_retrieveCurrentUser');
    console.time('COGNITO_retrieveCurrentUser');

    if (window.OAuthConfig?.enabled) {
      return new Promise((resolve, reject) => {
        const tokens = getTokens();

        // No full token set — but clearAccessAndIdTokens() (fired on beforeunload)
        // may have cleared the access+id tokens while keeping the refresh token.
        // For PKCE tokens, attempt a silent refresh now rather than falling through
        // to a prompt=none SSO redirect (which fails for Google users because auth-ui
        // never stores a session for the Google PKCE callback path).
        // SDK tokens cannot be refreshed from KeeperFA's context (wrong Cognito client)
        // so those fall through to silent SSO as before.
        if (!tokens) {
          const refreshToken = getRefreshToken();
          const tokenSource = getTokenSource();
          if (refreshToken && tokenSource === 'pkce') {
            refreshTokens(refreshToken)
              .then((response) => {
                const refreshed = {
                  accessToken: response.access_token,
                  idToken: response.id_token,
                  refreshToken: response.refresh_token || refreshToken,
                };
                saveTokens(refreshed);
                resolve(formatOAuthTokenData({
                  access_token: refreshed.accessToken,
                  id_token: refreshed.idToken,
                  refresh_token: refreshed.refreshToken,
                }));
              })
              .catch(() => {
                clearTokens();
                reject();
              });
            return;
          }
          reject();
          return;
        }

        // If the access token is still valid, return immediately.
        if (hasValidTokens()) {
          try {
            resolve(formatOAuthTokenData({
              access_token: tokens.accessToken,
              id_token: tokens.idToken,
              refresh_token: tokens.refreshToken,
            }));
          } catch (err) {
            reject(err);
          }
          return;
        }

        // Access token expired — attempt a silent refresh before returning.
        // Routes to the correct Cognito endpoint based on how tokens were obtained:
        //   'sdk'  → Cognito InitiateAuth API (SDK-issued refresh tokens)
        //   'pkce' → POST /oauth2/token (Hosted UI refresh tokens)
        const doRefresh = getTokenSource() === 'pkce'
          ? refreshTokens(tokens.refreshToken)
          : refreshTokensViaCognitoApi(tokens.refreshToken);

        doRefresh
          .then((response) => {
            const refreshed = {
              accessToken: response.access_token,
              idToken: response.id_token,
              refreshToken: response.refresh_token,
            };
            saveTokens(refreshed);
            resolve(formatOAuthTokenData({
              access_token: refreshed.accessToken,
              id_token: refreshed.idToken,
              refresh_token: refreshed.refreshToken,
            }));
          })
          .catch(() => {
            // Refresh token also expired or revoked — clear tokens and force re-login.
            clearTokens();
            reject();
          });
        return;
      });
    }

    // SDK path — unchanged
    const cognitoUser = getUserPool().getCurrentUser();

    return new Promise(function (resolve, reject) {
      if (cognitoUser === null) {
        reject();
        return;
      }

      cognitoUser.getSession(function (sessionErrors, session) {
        if (sessionErrors || !session.isValid()) {
          reject();
          return;
        }

        resolve(formatCognitoUserData(session));
      });
    });
  },

  /**
   * Create a Cognito SDK user session from raw tokens.
   *
   * OAuth path: persists tokens in oauthTokenStorage THEN falls through to the
   *   SDK path so that authenticated operations (changePassword etc.) work.
   * SDK path: constructs a CognitoUserSession and calls setSignInUserSession.
   *
   * @param {object} tokens
   * @param {string} tokens.access_token
   * @param {string} tokens.id_token
   * @param {string} tokens.refresh_token
   * @returns {Promise<void>}
   */
  createUserSession: (tokens) => {
    console.info('COGNITO_createUserSession');
    console.time('COGNITO_createUserSession');

    if (window.OAuthConfig?.enabled) {
      saveTokens({
        accessToken: tokens.access_token,
        idToken: tokens.id_token,
        refreshToken: tokens.refresh_token,
      });
      // Fall through to SDK path so authenticated ops work immediately
    }

    // SDK path (always runs — needed for authenticated operations)
    const AccessToken = new CognitoAccessToken({ AccessToken: tokens.access_token });
    const IdToken = new CognitoIdToken({ IdToken: tokens.id_token });
    const RefreshToken = new CognitoRefreshToken();
    const cachedSession = new CognitoUserSession({
      IdToken,
      AccessToken,
      RefreshToken,
      ClockDrift: undefined,
    });

    const cognitoUser = new CognitoUser({
      Username: IdToken.payload['cognito:username'],
      Pool: getUserPool(),
    });

    return new Promise((resolve) => {
      cognitoUser.setSignInUserSession(cachedSession);
      resolve();
    });
  },

  // -------------------------------------------------------------------------
  // SDK-only methods — NO OAuth shim. These require a live CognitoUserSession.
  // -------------------------------------------------------------------------

  retrieveUserAttributes: () => {
    console.info('COGNITO_retrieveUserAttributes');
    console.time('COGNITO_retrieveUserAttributes');

    if (window.OAuthConfig?.enabled) {
      // OAuth path: attributes are embedded in the stored id_token JWT.
      // The Cognito SDK getUserAttributes call requires a live SDK session which
      // does not exist in the OAuth flow — read directly from stored tokens instead.
      return new Promise((resolve, reject) => {
        const tokens = getTokens();
        if (!tokens) {
          reject();
          return;
        }
        try {
          const user = formatOAuthTokenData({
            access_token: tokens.accessToken,
            id_token: tokens.idToken,
            refresh_token: tokens.refreshToken,
          });
          resolve({ onboardingcomplete: user.onboardingcomplete });
        } catch {
          reject();
        }
      });
    }

    // SDK path — unchanged
    const cognitoUser = getUserPool().getCurrentUser();

    return new Promise(function (resolve, reject) {
      if (cognitoUser === null) {
        reject();
        return;
      }

      cognitoUser.getSession(function (sessionErrors, session) {
        if (sessionErrors || !session.isValid()) {
          reject();
          return;
        }

        cognitoUser.getUserAttributes(function (attributesError, attributesArray) {
          if (attributesError) {
            reject();
            return;
          }

          const attributesObject = formatCognitoAttributesData(attributesArray);
          resolve(attributesObject);
        });
      });
    });
  },

  verify: ({ email, code }) => {
    console.info('COGNITO_verify');
    console.time('COGNITO_verify');

    const cognitoUser = new CognitoUser({
      Username: email.toLowerCase(),
      Pool: getUserPool(),
    });

    console.time('TIME_calling_cognitoUser.confirmRegistration');

    const promiseTimeout = new Promise((res) => {
      setTimeout(res, 500, 'timeout');
    });

    const promiseFunction = new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(code, true, function (err) {
        console.timeEnd('TIME_calling_cognitoUser.confirmRegistration');
        if (err) {
          reject({ global: renderError(err) });
          return;
        }
        resolve();
      });
    });

    return Promise.all([promiseTimeout, promiseFunction]);
  },

  updateCrmId: (crmId) => {
    const cognitoUser = getUserPool().getCurrentUser();

    const attributeList = [
      new CognitoUserAttribute({ Name: 'custom:crmid', Value: crmId }),
    ];

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err) => {
        if (err) {
          reject({ global: 'Sorry, Something Went Wrong: Please try again later.' });
          return;
        }

        cognitoUser.updateAttributes(attributeList, function (updateErr) {
          if (updateErr) {
            reject({ global: renderError(updateErr) });
            return;
          }
          resolve();
        });
      });
    });
  },

  updateUserDetails: (userDetails) => {
    console.info('COGNITO_updateUserDetails');
    console.time('COGNITO_updateUserDetails');

    const cognitoUser = getUserPool().getCurrentUser();

    const attributeList = [
      new CognitoUserAttribute({ Name: 'given_name', Value: userDetails.given_name }),
      new CognitoUserAttribute({ Name: 'family_name', Value: userDetails.family_name }),
      // Mirror into custom attributes so the Pre-Token Generation Lambda can
      // restore these values after Google IdP overwrites the standard claims on login.
      new CognitoUserAttribute({ Name: 'custom:given_name', Value: userDetails.given_name }),
      new CognitoUserAttribute({ Name: 'custom:family_name', Value: userDetails.family_name }),
      new CognitoUserAttribute({ Name: 'birthdate', Value: userDetails.birthdate }),
      new CognitoUserAttribute({ Name: 'custom:is_first_login', Value: userDetails.isFirstLogin }),
    ];

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject({ global: 'Sorry, Something Went Wrong: Please try again later.' });
          return;
        }

        if (session.isValid()) {
          cognitoUser.updateAttributes(attributeList, function (updateErr) {
            if (updateErr) {
              reject({ global: renderError(updateErr) });
              return;
            }
            resolve();
          });
        }
      });
    });
  },

  changePassword: ({ oldPassword, newPassword }) => {
    console.info('COGNITO_changePassword');
    console.time('COGNITO_changePassword');

    const cognitoUser = getUserPool().getCurrentUser();

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject({ global: 'Sorry, Something Went Wrong: Please try again later.' });
          return;
        }

        if (session.isValid()) {
          cognitoUser.changePassword(oldPassword, newPassword, function (changeErr) {
            if (changeErr) {
              reject({ global: renderError(changeErr) });
              return;
            }
            resolve();
          });
        }
      });
    });
  },

  resendCode: ({ email }) => {
    console.info('COGNITO_resendCode');
    console.time('COGNITO_resendCode');

    const cognitoUser = new CognitoUser({
      Username: email.toLowerCase(),
      Pool: getUserPool(),
    });

    return new Promise((resolve, reject) => {
      cognitoUser.resendConfirmationCode(function (err) {
        if (err) {
          reject({ global: renderError(err) });
          return;
        }
        resolve();
      });
    });
  },

  resetPassword: ({ email }) => {
    console.info('COGNITO_resetPassword');
    console.time('COGNITO_resetPassword');

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });

    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: function () {
          resolve();
        },
        onFailure: function (err) {
          reject({ global: renderError(err) });
        },
      });
    });
  },

  createPassword: ({ email, code, newPassword }) => {
    console.info('COGNITO_createPassword');
    console.time('COGNITO_createPassword');

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });

    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: function () {
          resolve();
        },
        onFailure: function (err) {
          reject({ global: renderError(err) });
        },
      });
    });
  },

  changeEmail: (email) => {
    console.info('COGNITO_changeEmail');
    console.time('COGNITO_changeEmail');

    const cognitoUser = getUserPool().getCurrentUser();

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject({ global: 'Sorry, Something Went Wrong: Please try again later.' });
          return;
        }

        if (session.isValid()) {
          const attributeList = [
            new CognitoUserAttribute({
              Name: 'email',
              Value: email.newEmail.toLowerCase(),
            }),
          ];

          cognitoUser.updateAttributes(attributeList, function (updateErr) {
            if (updateErr) {
              reject({ global: renderError(updateErr) });
              return;
            }
            resolve();
          });
        }
      });
    });
  },

  verifyChangedEmail: (emailData) => {
    console.info('COGNITO_verifyChangedEmail');
    console.time('COGNITO_verifyChangedEmail');

    const cognitoUser = getUserPool().getCurrentUser();
    const verificationCode = emailData.code;

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject({ global: 'Sorry, Something Went Wrong: Please try again later.' });
          return;
        }

        if (session.isValid()) {
          cognitoUser.verifyAttribute('email', verificationCode, {
            onSuccess: function () {
              resolve();
            },
            onFailure: function (verifyErr) {
              if (verifyErr) {
                reject({ global: renderError(verifyErr) });
              }
            },
          });
        }
      });
    });
  },
};
